import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { getPortalContainer } from '../utils/domUtils';
import {
  PaymentDialogOverlay,
  PaymentDialog as StyledDialog,
  PaymentDialogHeader,
  PaymentDialogTitle,
  CloseButtonModal,
  PaymentDialogContent,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextArea,
  PaymentDialogFooter,
  CancelButton,
  SubmitButton
} from '../styled/Modals';
import styled from 'styled-components';

// Custom styled components for signature dialog
const SignatureDialogIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(219, 39, 119, 0.25) 100%);
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid rgba(236, 72, 153, 0.3);
  
  svg {
    width: 24px;
    height: 24px;
    color: #ec4899;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(219, 39, 119, 0.15) 100%);
  border: 1px solid rgba(236, 72, 153, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  
  strong {
    color: #ec4899;
  }
`;

const SignatureIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17c3.333-3.333 5.667-6.667 7-8 1.333 1.333 3.667 4.667 7 8"></path>
    <path d="M20 21H4"></path>
    <path d="M21 7l-5-5-9 9 5 5 9-9z"></path>
  </svg>
);

const SignatureRequestDialog = ({
  showDialog,
  setShowDialog,
  onSubmit,
  isLoading = false
}) => {
  const [purpose, setPurpose] = useState('');
  const [description, setDescription] = useState('');

  if (!showDialog) return null;

  const handleSubmit = () => {
    if (!purpose.trim()) {
      return;
    }
    onSubmit({ purpose: purpose.trim(), description: description.trim() });
    // Reset form
    setPurpose('');
    setDescription('');
  };

  const handleClose = () => {
    setPurpose('');
    setDescription('');
    setShowDialog(false);
  };

  const dialogContainer = getPortalContainer('signature-dialog-container');

  return ReactDOM.createPortal(
    <PaymentDialogOverlay onClick={handleClose}>
      <StyledDialog onClick={(e) => e.stopPropagation()}>
        <PaymentDialogHeader>
          <PaymentDialogTitle>Request Digital Signature</PaymentDialogTitle>
          <CloseButtonModal onClick={handleClose}>✕</CloseButtonModal>
        </PaymentDialogHeader>
        
        <PaymentDialogContent>
          <SignatureDialogIcon>
            <SignatureIcon />
          </SignatureDialogIcon>
          
          <FormGroup>
            <FormLabel htmlFor="signature-purpose">Purpose *</FormLabel>
            <FormInput
              id="signature-purpose"
              type="text"
              placeholder="e.g., Visa Application, Contract Agreement, Consent Form"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              autoFocus
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="signature-description">Additional Details (Optional)</FormLabel>
            <FormTextArea
              id="signature-description"
              placeholder="Provide any additional context for the client about why their signature is needed..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </FormGroup>
          
          <InfoBox>
            <strong>How it works:</strong><br />
            A signature request will be sent to the client in the chat. They can click on it to:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Draw their signature with mouse or touch</li>
              <li>Type their name for text-based signature</li>
              <li>Save the signature for future use</li>
            </ul>
          </InfoBox>
        </PaymentDialogContent>
        
        <PaymentDialogFooter>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <SubmitButton 
            onClick={handleSubmit}
            disabled={!purpose.trim() || isLoading}
            style={{
              background: purpose.trim() && !isLoading 
                ? 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'
                : 'rgba(100, 100, 100, 0.5)',
              cursor: purpose.trim() && !isLoading ? 'pointer' : 'not-allowed'
            }}
          >
            {isLoading ? 'Sending...' : '✍️ Send Signature Request'}
          </SubmitButton>
        </PaymentDialogFooter>
      </StyledDialog>
    </PaymentDialogOverlay>,
    dialogContainer
  );
};

export default SignatureRequestDialog;
